import { Lock, RotateCcw } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { AppError } from '@obraplan/core/application/errors/app-error';
import {
  PAGE_CATALOG,
  type PageDefinition,
  type PageSection,
  type TabDefinition,
} from '@obraplan/core/domain/access/pages';
import {
  PERMISSION_CATALOG,
  type KnownPermissionKey,
} from '@obraplan/core/domain/access/permissions';
import { isLockedRole, type Role } from '@obraplan/core/domain/access/role';
import { ROLE_CATALOG } from '@obraplan/core/domain/access/roles';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, Input } from '@/components/ui/input';
import { cn } from '@/lib/cn';
import {
  lockedBy,
  pagesOf,
  permissionLabel,
  selectPage,
  selectPermission,
  selectTab,
  unselectPage,
  unselectPermission,
  unselectTab,
  type Selection,
} from './role-editor';

export interface RoleEditorSubmit {
  readonly name: string;
  readonly description?: string;
  readonly permissions: readonly KnownPermissionKey[];
}

export interface RoleEditorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Ausente = papel novo. */
  role?: Role | undefined;
  /** Só olha: quem não administra papéis vê o que o papel abre, sem poder mudar. */
  readOnly?: boolean;
  isPending: boolean;
  error?: AppError | undefined;
  onSubmit: (input: RoleEditorSubmit) => void;
}

const SECTIONS: readonly PageSection[] = ['Operação', 'Configurações'];

/**
 * As permissões que não são tela nem aba — as leituras e ações, na ordem e nos grupos do
 * catálogo. As abas moram embaixo da tela delas, e só lá.
 */
const DATA_GROUPS = [
  ...new Set(
    PERMISSION_CATALOG.filter(
      (definition) => definition.group !== 'Telas' && definition.group !== 'Abas',
    ).map((definition) => definition.group),
  ),
].map((group) => ({
  group,
  permissions: PERMISSION_CATALOG.filter((definition) => definition.group === group),
}));

/**
 * O papel, pelas telas que ele abre.
 *
 * A primeira pergunta de quem configura um papel é "o que essa pessoa vai ver?", e não
 * "ela tem `employees.view`?". Então a tela vem primeiro — marcada, ela mostra o que se
 * pode fazer nela e o que ela lê —, e a lista completa de permissões fica embaixo, para
 * quem precisa do detalhe. As duas mexem no mesmo conjunto, e o que uma trava a outra
 * mostra travado, dizendo por quê.
 */
export function RoleEditorDialog({
  open,
  onOpenChange,
  role,
  readOnly = false,
  isPending,
  error,
  onSubmit,
}: RoleEditorDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selection, setSelection] = useState<Selection>(new Set());

  const locked = role !== undefined && isLockedRole(role);
  const disabled = readOnly || locked;
  const factory = ROLE_CATALOG.find((definition) => definition.key === role?.key);

  useEffect(() => {
    if (!open) return;
    setName(role?.name ?? '');
    setDescription(role?.description ?? '');
    setSelection(new Set(role?.permissions as readonly KnownPermissionKey[] | undefined));
  }, [open, role]);

  const pages = pagesOf(selection);
  const nameError =
    error?.fieldErrors.find((field) => field.field === 'name')?.message ??
    (error?.kind === 'conflict' ? error.message : undefined);
  const formError =
    error !== undefined && nameError === undefined && error.fieldErrors.length === 0
      ? error.message
      : undefined;

  const togglePage = (page: PageDefinition, on: boolean) =>
    setSelection((current) => (on ? selectPage(current, page) : unselectPage(current, page)));
  const toggleTab = (page: PageDefinition, tab: TabDefinition, on: boolean) =>
    setSelection((current) => (on ? selectTab(current, tab) : unselectTab(current, page, tab)));
  const togglePermission = (key: KnownPermissionKey, on: boolean) =>
    setSelection((current) =>
      on ? selectPermission(current, key) : unselectPermission(current, key),
    );

  const title =
    role === undefined ? 'Novo papel' : disabled ? role.name : `Editar papel · ${role.name}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Marque as telas que quem tiver este papel vai ver. Cada tela marcada leva junto o que
            ela precisa ler; embaixo dela, escolha as abas que aparecem e o que a pessoa pode fazer
            ali.
          </DialogDescription>
        </DialogHeader>

        <form
          className="flex flex-col gap-5"
          onSubmit={(event) => {
            event.preventDefault();
            if (disabled) return;
            onSubmit({
              name,
              ...(description.trim() === '' ? {} : { description }),
              permissions: [...selection],
            });
          }}
        >
          {locked && (
            <p className="flex items-start gap-2 rounded-md border border-(--color-border-subtle) bg-(--color-surface-muted) p-3 text-sm">
              <Lock className="mt-0.5 size-4 shrink-0" aria-hidden="true" />O Administrador tem
              sempre acesso completo e não se edita — é ele quem garante que alguém sempre consiga
              consertar um papel mal configurado.
            </p>
          )}

          <div className="grid gap-4 md:grid-cols-2">
            <Field
              label="Nome"
              htmlFor="role-name"
              required
              {...(nameError === undefined ? {} : { error: nameError })}
            >
              <Input
                id="role-name"
                value={name}
                disabled={disabled}
                maxLength={80}
                onChange={(event) => setName(event.target.value)}
              />
            </Field>
            <Field label="Descrição" htmlFor="role-description" hint="Para que serve este papel.">
              <Input
                id="role-description"
                value={description}
                disabled={disabled}
                maxLength={240}
                onChange={(event) => setDescription(event.target.value)}
              />
            </Field>
          </div>

          <p className="text-sm" aria-live="polite">
            {pages.length === 0 ? (
              <span className="text-(--color-state-danger)">
                Nenhuma tela marcada — quem tiver este papel entraria e não veria nada.
              </span>
            ) : (
              <>
                <span className="font-medium">
                  Abre {pages.length} de {PAGE_CATALOG.length} telas:
                </span>{' '}
                <span className="text-(--color-content-muted)">
                  {pages.map((page) => page.label).join(', ')}.
                </span>
              </>
            )}
          </p>

          {SECTIONS.map((section) => (
            <fieldset key={section} className="flex flex-col gap-2">
              <legend className="mb-2 text-xs font-semibold uppercase tracking-wide text-(--color-content-muted)">
                Telas · {section}
              </legend>
              <div className="grid gap-2 md:grid-cols-2">
                {PAGE_CATALOG.filter((page) => page.section === section).map((page) => (
                  <PageOption
                    key={page.permission}
                    page={page}
                    selection={selection}
                    disabled={disabled}
                    onPage={(on) => togglePage(page, on)}
                    onTab={(tab, on) => toggleTab(page, tab, on)}
                    onPermission={togglePermission}
                  />
                ))}
              </div>
            </fieldset>
          ))}

          <details className="rounded-md border border-(--color-border-subtle)">
            <summary className="cursor-pointer select-none px-3 py-2 text-sm font-medium">
              Todas as permissões (
              {
                [...selection].filter((key) => !key.startsWith('page.') && !key.startsWith('tab.'))
                  .length
              }{' '}
              marcadas)
            </summary>
            <div className="grid gap-4 border-t border-(--color-border-subtle) p-3 md:grid-cols-2">
              {DATA_GROUPS.map(({ group, permissions }) => (
                <div key={group} className="flex flex-col gap-1">
                  <p className="text-xs font-semibold uppercase tracking-wide text-(--color-content-muted)">
                    {group}
                  </p>
                  {permissions.map((definition) => (
                    <PermissionCheckbox
                      key={definition.key}
                      permission={definition.key}
                      selection={selection}
                      disabled={disabled}
                      onChange={togglePermission}
                    />
                  ))}
                </div>
              ))}
            </div>
          </details>

          {formError !== undefined && (
            <p role="alert" className="text-sm text-(--color-state-danger)">
              {formError}
            </p>
          )}

          <DialogFooter className="flex-wrap">
            {factory !== undefined && !disabled && (
              <Button
                type="button"
                variant="ghost"
                className="md:mr-auto"
                onClick={() => {
                  setName(factory.name);
                  setDescription(factory.description);
                  setSelection(new Set(factory.permissions));
                }}
              >
                <RotateCcw aria-hidden="true" />
                Restaurar o padrão
              </Button>
            )}
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              {disabled ? 'Fechar' : 'Cancelar'}
            </Button>
            {!disabled && (
              <Button
                type="submit"
                variant="primary"
                disabled={isPending || name.trim() === '' || pages.length === 0}
              >
                {isPending ? 'Salvando…' : role === undefined ? 'Criar papel' : 'Salvar'}
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PageOption({
  page,
  selection,
  disabled,
  onPage,
  onTab,
  onPermission,
}: {
  page: PageDefinition;
  selection: Selection;
  disabled: boolean;
  onPage: (on: boolean) => void;
  onTab: (tab: TabDefinition, on: boolean) => void;
  onPermission: (key: KnownPermissionKey, on: boolean) => void;
}) {
  const on = selection.has(page.permission);
  const id = `page-${page.permission}`;

  return (
    <div
      className={cn(
        'rounded-md border p-3 transition-colors',
        on ? 'border-(--color-brand) bg-(--color-surface-muted)' : 'border-(--color-border-subtle)',
      )}
    >
      <label htmlFor={id} className="flex cursor-pointer items-start gap-3">
        <input
          id={id}
          type="checkbox"
          className="mt-1 size-4 shrink-0"
          checked={on}
          disabled={disabled}
          onChange={(event) => onPage(event.target.checked)}
        />
        <span className="flex min-w-0 flex-col">
          <span className="font-medium">{page.label}</span>
          <span className="text-sm text-(--color-content-muted)">{page.summary}</span>
        </span>
      </label>

      {on && (
        <div className="mt-2 flex flex-col gap-1 pl-7">
          {page.tabs !== undefined && (
            // As abas antes das ações: "o que aparece" vem antes de "o que se faz" (RN-626).
            // Desmarcar a última desmarca a tela (RN-628).
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-(--color-content-muted)">
                Abas que aparecem:
              </span>
              {page.tabs.map((tab) => (
                <label
                  key={tab.permission}
                  className={cn('flex items-start gap-2 text-sm', !disabled && 'cursor-pointer')}
                >
                  <input
                    type="checkbox"
                    className="mt-0.5 size-4 shrink-0"
                    checked={selection.has(tab.permission)}
                    disabled={disabled}
                    onChange={(event) => onTab(tab, event.target.checked)}
                  />
                  <span className="min-w-0">{tab.label}</span>
                </label>
              ))}
            </div>
          )}
          {page.actions.length > 0 && (
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-(--color-content-muted)">
                Nesta tela, pode:
              </span>
              {page.actions.map((action) => (
                <PermissionCheckbox
                  key={action}
                  permission={action}
                  selection={selection}
                  disabled={disabled}
                  onChange={onPermission}
                />
              ))}
            </div>
          )}
          <span className="text-xs text-(--color-content-muted)">
            Lê: {page.reads.map(permissionLabel).join(' · ')}
          </span>
        </div>
      )}
    </div>
  );
}

function PermissionCheckbox({
  permission,
  selection,
  disabled,
  onChange,
}: {
  permission: KnownPermissionKey;
  selection: Selection;
  disabled: boolean;
  onChange: (key: KnownPermissionKey, on: boolean) => void;
}) {
  const checked = selection.has(permission);
  const holders = checked ? lockedBy(selection, permission) : [];
  const held = holders.length > 0;

  return (
    <label
      className={cn('flex items-start gap-2 text-sm', !disabled && !held && 'cursor-pointer')}
      title={held ? `Exigida por: ${holders.join(', ')}` : undefined}
    >
      <input
        type="checkbox"
        className="mt-0.5 size-4 shrink-0"
        checked={checked}
        disabled={disabled || held}
        onChange={(event) => onChange(permission, event.target.checked)}
      />
      <span className="min-w-0">
        {permissionLabel(permission)}
        {held && (
          <span className="block text-xs text-(--color-content-muted)">
            exigida por {holders.join(', ')}
          </span>
        )}
      </span>
    </label>
  );
}
